import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronLeft, ClipboardPaste, Copy, Eye, History, PanelRightClose, PanelRightOpen, Save } from "lucide-react";
import { rhm, legacy, apiError } from "../api";
import { useRhmAuth } from "../RhmAuthContext";
import { Badge, Btn, Field, Input, Textarea } from "../ui";
import { LiveSwitch } from "../components/LiveSwitch";
import { analyse, slugify, titleChecks } from "../../lib/blogSeo";
import { AddBlockBar, BlockCard, newBlock } from "./blogBlocks";
import { PreviewDialog, PublishDialog, SeoPanel, SettingsColumn, VersionsDialog } from "./blogEditorPanels";
import QuickBlog from "./QuickBlog";
import { useSaveShortcut, saveKeyLabel } from "../useSaveShortcut";
import { LivePreviewFrame } from "../components/LivePreviewFrame";
import { BLOG_PREVIEW_MESSAGE, BLOG_PREVIEW_SLUG } from "../../lib/blogPreview";

const STATUS_TONE = { published: "good", draft: "muted", review: "gold", scheduled: "gold",
    unpublished: "warn", archived: "danger" };

const SAVE_FIELDS = ["title", "slug", "excerpt", "category_id", "author_id", "tags", "featured_image",
    "social_image", "style", "blocks", "seo", "toc_enabled", "disclaimer_enabled", "disclaimer_text",
    "review", "product_slugs", "consultation_area", "related_post_ids", "is_featured"];

export default function RhmBlogEditor() {
    const { postId } = useParams();
    const navigate = useNavigate();
    const qc = useQueryClient();
    const { can } = useRhmAuth();
    const [post, setPost] = useState(null);
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [showPublish, setShowPublish] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [showLive, setShowLive] = useState(false);
    const [showVersions, setShowVersions] = useState(false);
    const [showPaste, setShowPaste] = useState(false);
    const [slugTouched, setSlugTouched] = useState(false);
    const timer = useRef(null);

    const { data: loaded } = useQuery({
        queryKey: ["blog-post-crm", postId],
        queryFn: async () => (await rhm.get(`/blog/posts/${postId}`)).data,
    });
    const { data: meta } = useQuery({ queryKey: ["blog-meta-crm"], queryFn: async () => (await rhm.get("/blog/meta")).data });
    const { data: products = [] } = useQuery({ queryKey: ["blog-products"], queryFn: async () => (await rhm.get("/products")).data });
    const { data: consult } = useQuery({
        queryKey: ["blog-consult-config"],
        queryFn: async () => (await legacy.get("/consult/config")).data?.areas || [],
        retry: false,
    });
    const { data: allPosts = [] } = useQuery({ queryKey: ["blog-all-posts"], queryFn: async () => (await rhm.get("/blog/posts?status=all")).data });

    useEffect(() => { if (loaded && !post) { setPost(loaded); setSlugTouched(true); } }, [loaded, post]);

    const report = useMemo(() => (post ? analyse(post) : { score: 0, checks: [], words: 0, reading_minutes: 1 }), [post]);

    // What a reader would see right now — the public article page renders this exact object.
    const previewPost = useMemo(() => {
        if (!post) return null;
        const author = (meta?.authors || []).find((a) => a.id === post.author_id) || {};
        const category = (meta?.categories || []).find((c) => c.id === post.category_id) || null;
        return {
            ...post,
            id: post.id || "preview",
            author,
            category,
            disclaimer: post.disclaimer_enabled === false ? "" : (post.disclaimer_text || ""),
            reading_minutes: report.reading_minutes || 1,
            published_at: post.published_at || new Date().toISOString(),
            toc_enabled: post.toc_enabled !== false,
            related: [],
            popular: [],
        };
    }, [post, meta, report]);

    const save = useCallback(async (source) => {
        if (!source) return;
        setSaving(true);
        try {
            const payload = {};
            SAVE_FIELDS.forEach((f) => { if (source[f] !== undefined) payload[f] = source[f]; });
            await rhm.put(`/blog/posts/${postId}`, payload);
            setSavedAt(new Date());
            setDirty(false);
            qc.invalidateQueries({ queryKey: ["blog-posts-crm"] });
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    }, [postId, qc]);

    // autosave
    useEffect(() => {
        if (!dirty || !post) return;
        clearTimeout(timer.current);
        timer.current = setTimeout(() => save(post), 1500);
        return () => clearTimeout(timer.current);
    }, [post, dirty, save]);

    // never lose the last keystrokes on reload or when leaving the editor
    const latest = useRef({ post: null, dirty: false });
    useEffect(() => { latest.current = { post, dirty }; }, [post, dirty]);
    useEffect(() => {
        const warn = (e) => { if (latest.current.dirty) { e.preventDefault(); e.returnValue = ""; } };
        window.addEventListener("beforeunload", warn);
        return () => {
            window.removeEventListener("beforeunload", warn);
            if (latest.current.dirty) save(latest.current.post);
        };
    }, [save]);

    // Ctrl/⌘ + S writes the post straight away instead of waiting for the autosave.
    useSaveShortcut(() => { if (post?.can_edit !== false) save(post); });

    const change = (patch) => {
        setPost((p) => ({ ...p, ...patch }));
        setDirty(true);
    };
    const setTitle = (title) => {
        const patch = { title };
        if (!slugTouched || !post.slug) patch.slug = slugify(title);
        if (!post.seo?.seo_title || post.seo.seo_title === post.title) patch.seo = { ...(post.seo || {}), seo_title: title };
        change(patch);
    };

    const setBlocks = (blocks) => change({ blocks });
    const addBlock = (type) => setBlocks([...(post.blocks || []), newBlock(type)]);
    const moveBlock = (i, dir) => {
        const list = [...post.blocks];
        const j = i + dir;
        if (j < 0 || j >= list.length) return;
        [list[i], list[j]] = [list[j], list[i]];
        setBlocks(list);
    };
    const duplicateBlock = (i) => {
        const list = [...post.blocks];
        list.splice(i + 1, 0, { ...JSON.parse(JSON.stringify(list[i])), id: `blk_${Math.random().toString(36).slice(2, 10)}` });
        setBlocks(list);
    };
    const deleteBlock = (i) => setBlocks(post.blocks.filter((_, j) => j !== i));

    const publish = async (scheduledFor) => {
        try {
            await save(post);
            const { data } = await rhm.post(`/blog/posts/${postId}/publish`, { scheduled_for: scheduledFor || "" });
            setPost((p) => ({ ...p, status: data.status, published_at: data.published_at, scheduled_for: data.scheduled_for }));
            setShowPublish(false);
            toast.success(scheduledFor ? "Article scheduled" : "Article published");
            qc.invalidateQueries({ queryKey: ["blog-posts-crm"] });
        } catch (e) { toast.error(apiError(e)); }
    };

    const setStatus = async (status) => {
        try {
            const { data } = await rhm.post(`/blog/posts/${postId}/status`, { status });
            setPost((p) => ({ ...p, status: data.status }));
            toast.success(`Article marked ${status}`);
            qc.invalidateQueries({ queryKey: ["blog-posts-crm"] });
        } catch (e) { toast.error(apiError(e)); }
    };

    const setLive = async (live) => {
        try {
            await rhm.post(`/blog/posts/${postId}/live`, { live });
            setPost((p) => ({ ...p, page_live: live }));
            toast.success(live ? "Article is live again" : "Article switched off");
            qc.invalidateQueries({ queryKey: ["blog-posts-crm"] });
        } catch (e) { toast.error(apiError(e)); }
    };

    const duplicate = async () => {
        try {
            const { data } = await rhm.post(`/blog/posts/${postId}/duplicate`);
            toast.success("Draft copy created");
            navigate(`/rhm/blog/${data.id}`);
            setPost(null);
        } catch (e) { toast.error(apiError(e)); }
    };

    const restore = async (versionId) => {
        try {
            const { data } = await rhm.post(`/blog/posts/${postId}/versions/${versionId}/restore`);
            setPost(data);
            setShowVersions(false);
            toast.success("Version restored");
        } catch (e) { toast.error(apiError(e)); }
    };

    if (!post) return <div className="mono text-xs text-zinc-500 p-8">LOADING EDITOR…</div>;
    const readOnly = post.can_edit === false;
    const areas = Array.isArray(consult) ? consult : [];

    return (
        <div data-testid="blog-editor">
            {/* top bar */}
            <div className="sticky top-0 z-20 -mx-5 md:-mx-8 px-5 md:px-8 py-3 bg-bg0/95 backdrop-blur border-b border-white/10
                            flex flex-wrap items-center gap-3">
                <button onClick={() => navigate("/rhm/blog")} data-testid="editor-back"
                    className="mono text-[10px] tracking-[0.16em] text-zinc-400 hover:text-gold inline-flex items-center gap-1.5">
                    <ChevronLeft className="w-3.5 h-3.5" /> BLOG
                </button>
                <Badge tone={STATUS_TONE[post.status] || "muted"}>{(post.status || "").toUpperCase()}</Badge>
                <LiveSwitch live={post.page_live !== false} disabled={readOnly}
                    testId="editor-live-switch" previewHref={`/blog/${post.slug}`}
                    onChange={setLive} />
                <span className="mono text-[10px] tracking-[0.16em] text-zinc-500" data-testid="editor-save-state">
                    {saving ? "SAVING…" : dirty ? "UNSAVED CHANGES"
                        : savedAt ? `SAVED ${savedAt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}` : "ALL CHANGES SAVED"}
                </span>
                <span className="mono text-[9px] tracking-[0.16em] text-zinc-600 hidden md:inline"
                    data-testid="editor-save-hint">{saveKeyLabel()} + S</span>
                <div className="flex flex-wrap items-center gap-2 ml-auto">
                    <Btn variant="ghost" onClick={() => setShowVersions(true)} data-testid="editor-versions">
                        <History className="w-3.5 h-3.5 mr-1 inline" /> HISTORY
                    </Btn>
                    <Btn variant="ghost" onClick={duplicate} data-testid="editor-duplicate">
                        <Copy className="w-3.5 h-3.5 mr-1 inline" /> DUPLICATE
                    </Btn>
                    <Btn variant="ghost" onClick={() => setShowLive((v) => !v)} data-testid="editor-live-preview">
                        {showLive ? <PanelRightClose className="w-3.5 h-3.5 mr-1 inline" />
                            : <PanelRightOpen className="w-3.5 h-3.5 mr-1 inline" />}
                        {showLive ? "HIDE PREVIEW" : "LIVE PREVIEW"}
                    </Btn>
                    <Btn variant="ghost" onClick={() => setShowPreview(true)} data-testid="editor-preview">
                        <Eye className="w-3.5 h-3.5 mr-1 inline" /> PREVIEW
                    </Btn>
                    {!readOnly && (
                        <Btn variant="ghost" onClick={() => setShowPaste(true)} data-testid="editor-paste">
                            <ClipboardPaste className="w-3.5 h-3.5 mr-1 inline" /> PASTE CONTENT
                        </Btn>
                    )}
                    {!readOnly && (
                        <Btn variant="ghost" onClick={() => save(post)} data-testid="editor-save">
                            <Save className="w-3.5 h-3.5 mr-1 inline" /> SAVE
                        </Btn>
                    )}
                    {post.status === "published" && can("blog.unpublish") && (
                        <Btn variant="ghost" onClick={() => setStatus("unpublished")} data-testid="editor-unpublish">UNPUBLISH</Btn>
                    )}
                    {post.status !== "published" && (
                        <Btn onClick={() => setShowPublish(true)} data-testid="editor-publish"
                            disabled={!can("blog.publish") && !can("blog.schedule")}>PUBLISH</Btn>
                    )}
                    {post.status === "published" && can("blog.publish") && (
                        <Btn onClick={() => publish("")} data-testid="editor-update">UPDATE LIVE</Btn>
                    )}
                    {post.status === "draft" && !can("blog.publish") && (
                        <Btn variant="ghost" onClick={() => setStatus("review")} data-testid="editor-submit-review">
                            SUBMIT FOR REVIEW
                        </Btn>
                    )}
                </div>
            </div>

            {readOnly && (
                <div className="mt-4 border border-orange-400/40 bg-orange-500/[0.07] p-3 text-sm text-orange-200">
                    You can view this article but only its author or an editor can change it.
                </div>
            )}

            <div className={`grid gap-5 mt-5 items-start ${
                showLive ? "xl:grid-cols-2" : "lg:grid-cols-[minmax(0,1fr)_360px]"}`}>
                {/* main editor */}
                <div className="space-y-4">
                    <div className="border border-white/10 bg-bg1 p-5 space-y-4">
                        <input value={post.title || ""} onChange={(e) => setTitle(e.target.value)} disabled={readOnly}
                            placeholder="Article title" data-testid="editor-title"
                            className="w-full bg-transparent font-display text-2xl md:text-3xl tracking-tight
                                       outline-none border-b border-white/10 focus:border-gold pb-2" />
                        <div className="flex flex-wrap gap-3" data-testid="title-seo-checks">
                            {titleChecks(post.title, post.seo?.focus_keyword).map((c) => (
                                <span key={c.label} className={`mono text-[9px] tracking-[0.14em] ${
                                    c.status === "good" ? "text-lime" : c.status === "warn" ? "text-orange-300" : "text-red-400"}`}>
                                    {c.status === "good" ? "✓" : "⚠"} {c.label.toUpperCase()}
                                </span>
                            ))}
                        </div>
                        <Field label="URL" hint={`rehealthme.com/blog/${post.slug || ""}`}>
                            <Input value={post.slug || ""} disabled={readOnly} data-testid="editor-slug"
                                onChange={(e) => { setSlugTouched(true); change({ slug: slugify(e.target.value) }); }} />
                        </Field>
                        <Field label="Excerpt" hint="One or two lines shown on the blog list and in search results">
                            <Textarea rows={2} value={post.excerpt || ""} disabled={readOnly} data-testid="editor-excerpt"
                                onChange={(e) => change({ excerpt: e.target.value })} />
                        </Field>
                    </div>

                    <div className="space-y-3" data-testid="editor-blocks">
                        {(post.blocks || []).map((b, i) => (
                            <BlockCard key={b.id} block={b} index={i} total={post.blocks.length}
                                products={products} areas={areas} posts={allPosts}
                                onChange={(nb) => setBlocks(post.blocks.map((x, j) => (j === i ? nb : x)))}
                                onMove={moveBlock} onDuplicate={duplicateBlock} onDelete={deleteBlock} />
                        ))}
                        {!readOnly && <AddBlockBar onAdd={addBlock} />}
                    </div>
                </div>

                {/* settings + SEO, or the live article preview */}
                {showLive ? (
                    <div className="xl:sticky xl:top-20 self-start" data-testid="blog-preview-pane">
                        <LivePreviewFrame testId="blog-live-preview" title="Article preview"
                            path={`/blog/${BLOG_PREVIEW_SLUG}?preview=1`} payload={previewPost}
                            message={BLOG_PREVIEW_MESSAGE} note="UPDATES AS YOU TYPE"
                            liveUrl={post.status === "published" ? `/blog/${post.slug}` : ""}
                            height="76vh" />
                    </div>
                ) : (
                    <div className="space-y-3 lg:sticky lg:top-20 self-start max-h-[calc(100vh-110px)] overflow-y-auto pr-1">
                        <SeoPanel post={post} report={report} onChange={change} canEditSeo={can("blog.seo_manage") && !readOnly} />
                        <SettingsColumn post={post} meta={meta} products={products} areas={areas} allPosts={allPosts}
                            onChange={change} can={can} />
                    </div>
                )}
            </div>

            {showPreview && <PreviewDialog post={post} meta={meta} onClose={() => setShowPreview(false)} />}
            {showPublish && (
                <PublishDialog post={post} report={report} onClose={() => setShowPublish(false)} onPublish={publish}
                    canPublish={can("blog.publish")} canSchedule={can("blog.schedule")} />
            )}
            {showVersions && (
                <VersionsDialog postId={postId} onClose={() => setShowVersions(false)}
                    canRestore={can("blog.edit_all")} onRestored={restore} />
            )}
            {showPaste && (
                <QuickBlog mode="append" onClose={() => setShowPaste(false)}
                    onAppend={(blocks) => setBlocks([...(post.blocks || []), ...blocks])} />
            )}
        </div>
    );
}
